import { useCallback, useEffect, useRef, useState } from "react";

interface IUsePressOptions {
  delay?: number;
  moveThreshold?: number;
  onPress?: () => void;
  onLongPress?: () => void;
}

const DEFAULT_DELAY = 500;
const DEFAULT_MOVE_THRESHOLD = 10;

export const usePress = ({
  delay = DEFAULT_DELAY,
  moveThreshold = DEFAULT_MOVE_THRESHOLD,
  onPress,
  onLongPress,
}: IUsePressOptions) => {
  const startPosition = useRef({ x: 0, y: 0 });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True from pointer down until the gesture is resolved (long press fired, moved away, cancelled or released).
  const isPressPending = useRef(false);
  const [isPressed, setIsPressed] = useState(false);

  const clearLongPress = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const cancelPress = useCallback(() => {
    clearLongPress();
    isPressPending.current = false;
    setIsPressed(false);
  }, [clearLongPress]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.pointerType === "mouse" && event.button !== 0) {
        return;
      }

      startPosition.current = {
        x: event.clientX,
        y: event.clientY,
      };

      clearLongPress();
      isPressPending.current = true;
      setIsPressed(true);

      // Long press is only relevant for touch/stylus, mouse only triggers short press.
      if (!onLongPress || event.pointerType === "mouse") {
        return;
      }

      timer.current = setTimeout(() => {
        timer.current = null;
        isPressPending.current = false;
        onLongPress();
      }, delay);
    },
    [clearLongPress, delay, onLongPress],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!isPressPending.current) {
        return;
      }

      const dx = event.clientX - startPosition.current.x;
      const dy = event.clientY - startPosition.current.y;

      if (Math.hypot(dx, dy) > moveThreshold) {
        cancelPress();
      }
    },
    [cancelPress, moveThreshold],
  );

  const handlePointerUp = useCallback(() => {
    const shouldFirePress = isPressPending.current;
    cancelPress();
    if (shouldFirePress) {
      onPress?.();
    }
  }, [cancelPress, onPress]);

  useEffect(() => {
    return clearLongPress;
  }, [clearLongPress]);

  return {
    isPressed,
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: cancelPress,
    onPointerLeave: cancelPress,
  };
};
